import { useRef, useState } from "react";
import { toast } from "sonner";
import { AlertTriangle, Loader2, Play, Upload, X } from "lucide-react";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { useI18n } from "@/i18n/I18nContext";
import { translateError } from "@/i18n/errors";
import { useSpace } from "@/context/SpaceContext";
import { abortUpload, sendChunks, startUpload, uploadStatus } from "@/lib/chunkedUpload";
import { FilePick } from "@/components/admin/FilePick";
import { DriveImportButton } from "@/components/admin/DriveImportButton";

export const ApkUploader = ({ appId, onDone }) => {
  const { t, fmtBytes } = useI18n();
  const { base } = useSpace();
  const APK_ERROR = t("err.apkOnly");
  const [file, setFile] = useState(null);
  const [version, setVersion] = useState("");
  const [editVersion, setEditVersion] = useState(false);
  const [progress, setProgress] = useState(null);
  const [error, setError] = useState("");
  const [paused, setPaused] = useState(null); // { uploadId, chunkSize, totalChunks }
  const ctrl = useRef(null);
  const fileInput = useRef(null);

  const pick = (files, e) => {
    const f = files[0] || null;
    setError("");
    setPaused(null);
    if (f && !f.name.toLowerCase().endsWith(".apk")) {
      setFile(null);
      setError(APK_ERROR);
      if (e) e.target.value = "";
      return;
    }
    setFile(f);
  };

  const reset = () => {
    setFile(null);
    setVersion("");
    setEditVersion(false);
    setProgress(null);
    setPaused(null);
    if (fileInput.current) fileInput.current.value = "";
  };

  const run = async (session, received) => {
    ctrl.current = new AbortController();
    setError("");
    try {
      const res = await sendChunks({
        base, uploadId: session.uploadId, file, chunkSize: session.chunkSize, totalChunks: session.totalChunks,
        received, onProgress: setProgress, signal: ctrl.current.signal,
      });
      toast.success(t("upload.published", { v: res.version.label || res.version.version }));
      if (res.dedup) toast.info(t("upload.dedup"));
      if (res.removed_versions?.length) toast.info(t("upload.retention", { list: res.removed_versions.join(", ") }));
      reset();
      onDone(res.version);
    } catch (err) {
      if (err?.name === "AbortError") {
        await abortUpload(session.uploadId, base);
        setProgress(null);
        setError(t("upload.cancelled"));
        return;
      }
      if (err?.response?.status >= 400 && err.response.status < 500) {
        setProgress(null);
        setPaused(null);
        setError(translateError(err, t));
        return;
      }
      setPaused(session);
      setError(`${err?.response ? translateError(err, t) : t("upload.interrupted")} — ${t("upload.resumable")}`);
    }
  };

  const submit = async (e) => {
    e.preventDefault();
    if (!file) return setError(APK_ERROR);
    setProgress(0);
    try {
      const init = await startUpload({ base, appId, file, version: editVersion ? version.trim() : "" });
      await run({ uploadId: init.upload_id, chunkSize: init.chunk_size, totalChunks: init.total_chunks }, []);
    } catch (err) {
      setProgress(null);
      setError(translateError(err, t));
    }
  };

  const resume = async () => {
    try {
      const st = await uploadStatus(paused.uploadId, base);
      await run(paused, st.received);
    } catch (err) {
      setPaused(null);
      setProgress(null);
      setError(translateError(err, t));
    }
  };

  const busy = progress !== null && !paused;
  return (
    <form onSubmit={submit} className="card-carbon rounded-2xl p-6" data-testid="apk-uploader">
      <h2 className="flex items-center gap-2 font-display text-sm font-bold uppercase tracking-wider text-white">
        <Upload className="h-5 w-5 text-[var(--gold)]" /> {t("upload.title")}
      </h2>
      <p className="mt-2 text-xs text-neutral-400">{t("upload.hint")}</p>
      <div className="mt-5 min-w-0 space-y-2">
        <Label htmlFor="apk-file">{t("upload.file")}</Label>
        <FilePick id="apk-file" inputRef={fileInput} accept=".apk,application/vnd.android.package-archive" files={file ? [file] : null} disabled={busy} onFiles={pick} testId="apk-file-input" />
      </div>
      <div className="mt-3 text-xs text-neutral-400">
        {editVersion ? (
          <div className="max-w-xs space-y-2">
            <Label htmlFor="apk-version">{t("form.versionOverride")}</Label>
            <Input id="apk-version" placeholder="1.0" maxLength={40} value={version} disabled={busy} onChange={(e) => setVersion(e.target.value)} data-testid="apk-version-input" />
          </div>
        ) : (
          <p>{t("form.versionAuto")}{" "}
            <button type="button" className="text-[var(--gold)] underline" onClick={() => setEditVersion(true)} data-testid="apk-version-edit-btn">{t("form.editVersion")}</button>
          </p>
        )}
      </div>
      {file && <p className="mt-2 break-all text-xs text-neutral-400" data-testid="apk-file-info">{file.name} · {fmtBytes(file.size)}</p>}
      {error && (
        <p className="mt-4 flex items-start gap-2 rounded-xl border border-red-500/50 bg-red-950/60 px-4 py-3 text-sm text-red-100" role="alert" data-testid="apk-upload-error">
          <AlertTriangle className="mt-0.5 h-4 w-4 shrink-0" /> {error}
        </p>
      )}
      {progress !== null && (
        <div className="mt-5" data-testid="apk-upload-progress">
          <div className="mb-1 flex justify-between text-xs">
            <span className="text-neutral-400">{t(paused ? "upload.paused" : "upload.uploading")}</span>
            <span className="font-display font-bold text-[var(--gold-bright)]" data-testid="apk-upload-percent">{Math.floor(progress)}%</span>
          </div>
          <div className="h-2.5 overflow-hidden rounded-full bg-black/60 ring-1 ring-[rgba(245,197,66,0.25)]">
            <div className="progress-fill h-full rounded-full" style={{ width: `${progress}%` }} />
          </div>
        </div>
      )}
      <div className="mt-5 flex flex-wrap items-start gap-2">
        {!busy && !paused && <DriveImportButton appId={appId} onDone={onDone} />}
        {!busy && !paused && (
          <button type="submit" className="btn-gold btn-sm" disabled={!file || (editVersion && !version.trim())} data-testid="apk-upload-submit-btn">
            <Upload className="h-4 w-4" /> {t("upload.submit")}
          </button>
        )}
        {busy && (
          <button type="button" className="btn-red btn-sm" onClick={() => ctrl.current?.abort()} data-testid="apk-upload-cancel-btn">
            <Loader2 className="h-4 w-4 animate-spin" /> {t("upload.cancel")}
          </button>
        )}
        {paused && (
          <>
            <button type="button" className="btn-gold btn-sm" onClick={resume} data-testid="apk-upload-resume-btn"><Play className="h-4 w-4" /> {t("upload.resume")}</button>
            <button type="button" className="btn-red btn-sm" onClick={async () => { await abortUpload(paused.uploadId, base); reset(); }} data-testid="apk-upload-abort-btn"><X className="h-4 w-4" /> {t("upload.abort")}</button>
          </>
        )}
      </div>
    </form>
  );
};
